/**
 * FleetManager V3 - Navigation Configuration
 */

export const NAVIGATION = {
  groups: {
    fleet: {
      label: 'Mon Parc',
      order: 1,
      defaultExpanded: false,
      items: [
        { id: 'dashboard', icon: 'LayoutDashboard', label: 'Tableau de bord' },
        { id: 'vehicles', icon: 'Car', label: 'V\u00e9hicules', showCount: true },
        { id: 'drivers', icon: 'Users', label: 'Conducteurs', showCount: true },
        { id: 'teams', icon: 'Users', label: '\u00c9quipes', showCount: true },
        { id: 'sites', icon: 'MapPin', label: 'Sites', showCount: true },
        { id: 'tools', icon: 'Hammer', label: 'Outillage' },
        { id: 'telematics', icon: 'MapPin', label: 'T\u00e9l\u00e9matique' }
      ]
    },
    operations: {
      label: 'Op\u00e9rations',
      order: 2,
      defaultExpanded: false,
      items: [
        { id: 'pipeline', icon: 'AlertTriangle', label: 'Alertes & t\u00e2ches', showCount: true },
        { id: 'maintenance', icon: 'Wrench', label: 'Maintenance' },
        { id: 'inspections', icon: 'ClipboardCheck', label: 'Inspections' },
        { id: 'fuel', icon: 'Fuel', label: 'Carburant' },
        { id: 'fines', icon: 'Scale', label: 'Amendes ANTAI', showCount: true },
        { id: 'events', icon: 'Calendar', label: 'Calendrier' }
      ]
    },
    insurance: {
      label: 'Administratif',
      order: 3,
      defaultExpanded: false,
      items: [
        { id: 'insurance', icon: 'Shield', label: 'Assurances', showCount: true },
        { id: 'documents', icon: 'FileText', label: 'Documents' },
        { id: 'compliance', icon: 'ShieldCheck', label: 'RGPD' }
      ]
    },
    finance: {
      label: 'Finance & Rapports',
      order: 4,
      defaultExpanded: false,
      items: [
        { id: 'costs', icon: 'CreditCard', label: 'Co\u00fbts' },
        // La vue budget existait sans aucun chemin pour l'atteindre : rendue par
        // App.jsx mais déclarée nulle part ici, donc absente de tous les menus,
        // quel que soit le profil.
        { id: 'budget', icon: 'Wallet', label: 'Budgets' },
        { id: 'suppliers', icon: 'Building2', label: 'Fournisseurs' },
        { id: 'reports', icon: 'BarChart3', label: 'Rapports' },
        { id: 'report-archive', icon: 'Archive', label: 'Archives' }
      ]
    },
    administration: {
      label: 'Configuration',
      order: 5,
      adminOnly: true,
      defaultExpanded: false,
      items: [
        { id: 'settings', icon: 'Settings', label: 'Param\u00e8tres' },
        { id: 'insurance-config', icon: 'Shield', label: 'Config Assurance' },
        { id: 'profile_admin', icon: 'Users', label: 'Profils UI' },
        { id: 'emails', icon: 'Mail', label: 'Emails' },
        { id: 'campaigns', icon: 'Send', label: 'Campagnes' },
        { id: 'shares', icon: 'Share2', label: 'Partages' }
      ]
    },
    platform: {
      label: 'Plateforme',
      order: 6,
      superuserOnly: true,
      defaultExpanded: false,
      items: [
        { id: 'superadmin', icon: 'Shield', label: 'SuperAdmin', superuserOnly: true },
        { id: 'radar', icon: 'Server', label: 'App Radar' },
        { id: 'feature_monitor', icon: 'Activity', label: 'Live Monitor' },
        { id: 'sidebar-config', icon: 'LayoutList', label: 'Config Sidebar' }
      ]
    }
  }
};

/**
 * Get filtered navigation based on profile modules
 * @param {string[]} profileModules - Array of module IDs the profile has access to
 * @param {string} navStyle - Navigation style (minimal, grouped, full)
 * @param {object} options - Additional options
 * @param {boolean} options.isAdmin - Whether the user is an admin (shows admin-only groups)
 * @param {boolean} options.isSuperuser - Whether the user is a superuser (shows platform groups)
 */
export const getFilteredNavigation = (profileModules, navStyle, options = {}) => {
  const { isAdmin = false, isSuperuser = false } = options;
  const result = {};

  Object.entries(NAVIGATION.groups).forEach(([groupKey, group]) => {
    // Skip admin-only groups for non-admins
    if (group.adminOnly && !isAdmin) {
      return;
    }

    // Skip superuser-only groups for non-superusers
    if (group.superuserOnly && !isSuperuser) {
      return;
    }

    // For admin/superuser groups, show all items (they're not module-controlled)
    let visibleItems;
    if (group.adminOnly || group.superuserOnly) {
      visibleItems = group.items.filter(item => {
        // Filter superuser-only items within the group
        if (item.superuserOnly && !isSuperuser) {
          return false;
        }
        return true;
      });
    } else {
      visibleItems = group.items.filter(item => profileModules.includes(item.id));
    }

    if (visibleItems.length > 0) {
      result[groupKey] = {
        ...group,
        items: visibleItems
      };
    }
  });

  return result;
};

/**
 * Get flat navigation (for minimal style)
 */
export const getFlatNavigation = (profileModules) => {
  const allItems = [];

  Object.values(NAVIGATION.groups)
    .sort((a, b) => a.order - b.order)
    .forEach(group => {
      group.items.forEach(item => {
        if (profileModules.includes(item.id)) {
          allItems.push(item);
        }
      });
    });

  return allItems;
};

/**
 * Apply custom group labels to navigation
 * @param {object} navigation - Navigation object (from getFilteredNavigation)
 * @param {object} customLabels - Custom labels object (e.g., {"main": "My Vehicles"})
 * @returns {object} Navigation with custom labels applied
 */
export const applyCustomLabels = (navigation, customLabels = {}) => {
  if (!customLabels || Object.keys(customLabels).length === 0) {
    return navigation;
  }

  const result = {};
  Object.entries(navigation).forEach(([key, group]) => {
    result[key] = {
      ...group,
      label: customLabels[key] || group.label,
    };
  });

  return result;
};

/**
 * Get default module dependencies
 * @returns {object} Default module dependencies
 */
export const getDefaultModuleDependencies = () => ({
  insurance: ['vehicles'],  // Claims are now part of insurance module
  fuel: ['vehicles', 'drivers'],
  inspections: ['vehicles'],
  maintenance: ['vehicles'],
  telematics: ['vehicles'],
  reports: ['vehicles'],
});

/**
 * Check if selected modules satisfy all dependencies
 * @param {string[]} selectedModules - Array of selected module IDs
 * @param {object} dependencies - Module dependencies object
 * @returns {object} Object with { valid: boolean, missing: [{ module, requires }] }
 */
export const validateModuleDependencies = (selectedModules, dependencies = {}) => {
  const effectiveDeps = { ...getDefaultModuleDependencies(), ...dependencies };
  const missing = [];

  selectedModules.forEach(moduleId => {
    const deps = effectiveDeps[moduleId] || [];
    deps.forEach(depId => {
      if (!selectedModules.includes(depId)) {
        missing.push({ module: moduleId, requires: depId });
      }
    });
  });

  return {
    valid: missing.length === 0,
    missing,
  };
};
